import React, { useEffect, useState, useRef } from "react";
import { Modal, Spin, message, Button } from "antd";
import { PrinterOutlined, MailOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { apiGetInvoiceData } from "services/dashboardService";
import "./InvoiceModal.css";

const InvoiceModal = ({ visible, onClose, transactionId, registrationName, onEmailReceipt }) => {
  const userDataStore = useSelector((x) => x.userDataStore);
  const appDataStore = useSelector((x) => x.appDataStore);
  const [loading, setLoading] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [invoiceData, setInvoiceData] = useState(null);
  const printRef = useRef(null);

  const handlePrint = () => {
    const printContent = printRef.current;
    if (!printContent) return;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Invoice #${invoiceData?.receipt_number || invoiceData?.registration_id || transactionId}</title>
          <style>
            @page { size: portrait; margin: 20mm; }
            body { font-family: Arial, sans-serif; margin: 0; padding: 20px; }
            .invoice-header { background-color: #60A8DC; color: white; padding: 20px; text-align: center; }
            .invoice-header img { max-width: 150px; margin-bottom: 10px; }
            .invoice-header h1 { margin: 0; font-size: 24px; }
            .invoice-content { padding: 20px; }
            .invoice-content h2 { color: #333; margin-top: 0; }
            .invoice-info-row { display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #eee; }
            .invoice-info-label { font-weight: bold; color: #555; }
            .invoice-info-value { color: #333; }
            .invoice-items-table { width: 100%; border-collapse: collapse; margin: 20px 0; }
            .invoice-items-table th { background-color: #f8f8f8; padding: 12px; text-align: left; border-bottom: 2px solid #ddd; }
            .invoice-items-table td { padding: 12px; border-bottom: 1px solid #eee; }
            .invoice-totals { width: 100%; margin-top: 20px; }
            .invoice-footer { background-color: #f8f8f8; padding: 10px 15px; text-align: center; color: #777; font-size: 11px; }
            .invoice-footer p { margin: 2px 0; }
            @media print { body { print-color-adjust: exact; -webkit-print-color-adjust: exact; } }
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  useEffect(() => {
    if (visible && transactionId) {
      fetchInvoiceData();
    }
  }, [visible, transactionId]);

  const fetchInvoiceData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("token", userDataStore.token);
      params.append("transaction_id", transactionId);
      params.append("registration_id", appDataStore.curRegistration?.ID);

      const res = await apiGetInvoiceData(params);
      // axiosPost returns data.data directly on success, so res IS the invoice data
      if (res) {
        setInvoiceData(res);
      } else {
        message.error("Failed to load invoice data");
      }
    } catch (err) {
      console.error("Error fetching invoice data:", err);
      message.error("Error loading invoice");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp * 1000);
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  const formatCurrency = (amount) => {
    const num = parseFloat(amount) || 0;
    return num.toFixed(2);
  };

  const handleEmailReceipt = async () => {
    if (!onEmailReceipt || !transactionId) return;
    setEmailLoading(true);
    try {
      await onEmailReceipt(transactionId);
    } finally {
      setEmailLoading(false);
    }
  };

  return (
    <Modal
      visible={visible}
      onCancel={onClose}
      footer={null}
      width={700}
      style={{ top: 20 }}
      bodyStyle={{ padding: 0, maxHeight: "90vh", overflow: "auto" }}
      className="invoice-popup-modal"
      closable={true}
      maskClosable={true}
    >
      <Spin spinning={loading}>
        {invoiceData ? (
          <div className="invoice-container">
            <div className="invoice-email-container" ref={printRef}>
              {/* Header */}
              <div className="invoice-header">
                {invoiceData.image_url && (
                  <img src={invoiceData.image_url} alt="Logo" />
                )}
                <h1>Invoice #{invoiceData.receipt_number || invoiceData.registration_id || transactionId}</h1>
              </div>

              {/* Content */}
              <div className="invoice-content">
                <h2>Registration Invoice</h2>

                {/* Registration Info */}
                <div className="invoice-info-row">
                  <span className="invoice-info-label">Invoice For:</span>
                  <span className="invoice-info-value">{invoiceData.receipt_for || ""}</span>
                </div>
                <div className="invoice-info-row">
                  <span className="invoice-info-label">Registration:</span>
                  <span className="invoice-info-value">{invoiceData.registration_name || registrationName || ""}</span>
                </div>
                <div className="invoice-info-row">
                  <span className="invoice-info-label">Invoice #:</span>
                  <span className="invoice-info-value">{invoiceData.receipt_number || invoiceData.registration_id || transactionId}</span>
                </div>
                <div className="invoice-info-row">
                  <span className="invoice-info-label">Date:</span>
                  <span className="invoice-info-value">{invoiceData.registration_date || formatDate(invoiceData.created_at)}</span>
                </div>
                {invoiceData.process_date && (
                  <div className="invoice-info-row">
                    <span className="invoice-info-label">Invoice Date:</span>
                    <span className="invoice-info-value">{invoiceData.process_date}</span>
                  </div>
                )}

                {/* Items Table */}
                {invoiceData.history_list && invoiceData.history_list.length > 0 && (
                  <table className="invoice-items-table">
                    <thead>
                      <tr>
                        <th style={{ width: "15%" }}>Participant</th>
                        <th style={{ width: "45%" }}>Description</th>
                        <th style={{ width: "20%", textAlign: "right" }}>Amount</th>
                        <th style={{ width: "20%", textAlign: "right" }}>Fee</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoiceData.history_list.map((item, index) => (
                        <tr key={index}>
                          <td>{item.child_num || ""}</td>
                          <td className="invoice-description-cell">
                            {item.description && item.description.split("\\n").map((line, i) => (
                              <div key={i}>{line}</div>
                            ))}
                            {item.extraprograms && (
                              <small style={{ color: "#777" }}>{item.extraprograms}</small>
                            )}
                          </td>
                          <td style={{ textAlign: "right" }}>${item.amount || "0.00"}</td>
                          <td style={{ textAlign: "right" }}>${item.est_fee || "0.00"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}

                {/* Totals */}
                <table className="invoice-totals">
                  {invoiceData.sub_total !== undefined && (
                    <tr>
                      <td style={{ padding: "8px 0", fontSize: "14px", textAlign: "left" }}>Subtotal:</td>
                      <td style={{ padding: "8px 0", fontSize: "14px", textAlign: "right" }}>${formatCurrency(invoiceData.sub_total)}</td>
                    </tr>
                  )}
                  {invoiceData.processing_fee !== undefined && (
                    <tr>
                      <td style={{ padding: "8px 0", fontSize: "14px", textAlign: "left" }}>Processing Fee:</td>
                      <td style={{ padding: "8px 0", fontSize: "14px", textAlign: "right" }}>${formatCurrency(invoiceData.processing_fee)}</td>
                    </tr>
                  )}
                  {invoiceData.total !== undefined && (
                    <tr style={{ borderTop: "2px solid #ddd" }}>
                      <td style={{ padding: "15px 0 8px 0", fontSize: "18px", fontWeight: "bold", color: "#60A8DC", textAlign: "left" }}>
                        Total Paid:
                      </td>
                      <td style={{ padding: "15px 0 8px 0", fontSize: "18px", fontWeight: "bold", color: "#60A8DC", textAlign: "right" }}>
                        ${formatCurrency(invoiceData.total)}
                      </td>
                    </tr>
                  )}
                </table>
              </div>

              {/* Footer */}
              <div className="invoice-footer">
                <p>This is an automated invoice. Please do not reply to this email.</p>
                <p>
                  If you have any questions, please contact us at{" "}
                  <a href={`mailto:info@${invoiceData.domain || "beclutch.club"}`}>
                    info@{invoiceData.domain || "beclutch.club"}
                  </a>
                </p>
                <p>&copy; {new Date().getFullYear()} BeClutch Camp. All rights reserved.</p>
              </div>
            </div>
            {/* Print Button */}
            <div className="invoice-print-button-container">
              <Button
                type="default"
                size="large"
                icon={<MailOutlined />}
                onClick={handleEmailReceipt}
                loading={emailLoading}
                style={{ marginRight: 12 }}
              >
                Email Invoice
              </Button>
              <Button
                type="primary"
                size="large"
                icon={<PrinterOutlined />}
                onClick={handlePrint}
                className="invoice-print-button"
              >
                Print Invoice
              </Button>
            </div>
          </div>
        ) : (
          !loading && (
            <div style={{ padding: 40, textAlign: "center" }}>
              No invoice data available
            </div>
          )
        )}
      </Spin>
    </Modal>
  );
};

export default InvoiceModal;
